import React from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material'
import LoginRoundedIcon from '@mui/icons-material/LoginRounded'
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded'
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded'
import { motion } from 'motion/react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { APP_DATA } from '../../data'
import { useAuth } from '../../context/AuthContext'
import { getRutaInicialSesion } from '../../utils/permisos'
import LogoClasol from '../../media/logo_clasol_trasparente_1.png'
import { cardTransition } from '../../components/animation/motionPresets'

const getApiErrorMessage = (error) => (
  Array.isArray(error?.response?.data?.details) && error.response.data.details.length > 0
    ? error.response.data.details.join('\n')
    : error?.response?.data?.message || 'No se ha podido iniciar sesión'
)

const getPostLoginPath = (sessionResponse, fromPath) => {
  const rolePath = getRutaInicialSesion(sessionResponse)
  return rolePath === '/admin' && fromPath?.startsWith('/admin') ? fromPath : rolePath
}

export const Login = () => {
  const { authenticated, loadingSession, login, session } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [showPassword, setShowPassword] = React.useState(false)
  const [form, setForm] = React.useState({
    login: '',
    password: '',
  })
  const [submitting, setSubmitting] = React.useState(false)
  const [errorMessage, setErrorMessage] = React.useState('')

  if (!loadingSession && authenticated) {
    return <Navigate to={getPostLoginPath(session, location.state?.from?.pathname)} replace />
  }

  const handleChange = (event) => {
    const { name, value } = event.target
    setForm((currentForm) => ({
      ...currentForm,
      [name]: value,
    }))
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    setErrorMessage('')
    setSubmitting(true)

    try {
      const sessionResponse = await login({
        login: form.login.trim(),
        password: form.password,
      })
      navigate(getPostLoginPath(sessionResponse, location.state?.from?.pathname), { replace: true })
    } catch (error) {
      setErrorMessage(getApiErrorMessage(error))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Box className="login-page">
      <Box
        component={motion.div}
        className="login-brand-panel"
        initial={{ opacity: 0, x: -12 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
      >
        <img className="login-brand-logo" src={LogoClasol} alt="Clasol" />
        <Typography className="login-brand-title">{APP_DATA.appName}</Typography>
        <Typography className="login-brand-subtitle">
          Plataforma central para aplicaciones operativas, integraciones y gestión de usuarios.
        </Typography>
      </Box>

      <Card component={motion.div} className="login-card" {...cardTransition}>
        <CardContent>
          <Typography variant="h4" className="login-title">Iniciar sesión</Typography>
          <Typography className="login-subtitle">
            Accede con tu usuario de Clasol Suite Apps.
          </Typography>

          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} className="login-form">
            <TextField
              label="Usuario o email"
              name="login"
              value={form.login}
              onChange={handleChange}
              fullWidth
              required
              autoComplete="username"
            />

            <TextField
              label="Contraseña"
              name="password"
              value={form.password}
              onChange={handleChange}
              type={showPassword ? 'text' : 'password'}
              fullWidth
              required
              autoComplete="current-password"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword((visible) => !visible)} edge="end">
                      {showPassword ? <VisibilityOffRoundedIcon /> : <VisibilityRoundedIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={submitting}
              endIcon={<LoginRoundedIcon />}
            >
              {submitting ? 'Entrando...' : 'Entrar'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  )
}
